/**
 * What a role covers, said in a line (WALLIYO-0131).
 *
 * Listing every grant was honest and useless: the Owner holds every feature
 * there is, and four dozen chips in a table cell is a wall nobody reads — on
 * the screen whose whole question is "what is the difference between these
 * roles".
 *
 * So the cell answers that question instead: how much, how sensitive, and
 * where. The full list stays one click away, because summarising is not the
 * same as hiding.
 */

import type { StaffRoleListItem } from "@wallyo/contracts/console";
import { SETTINGS } from "@wallyo/contracts/settings";
import type { SettingCategory } from "@wallyo/contracts/settings";

/** Every grantable staff feature, by id, with the category it belongs to. */
const CATEGORY_OF = new Map<string, SettingCategory>(
  SETTINGS.flatMap((setting) =>
    setting.kind === "feature_enablement" &&
    setting.settingId.endsWith("_STAFF_FEATURE_ENABLEMENT") &&
    setting.status !== "retired"
      ? [[setting.settingId, setting.category] as const]
      : [],
  ),
);

/** How many there are. A role holding all of them holds everything. */
export const TOTAL_FEATURES = CATEGORY_OF.size;

export type RoleArea = { category: SettingCategory; count: number };

export type RoleSummary = {
  features: number;
  permissions: number;
  /** True when it holds every feature there is: say so rather than count. */
  everything: boolean;
  /** How many of its features are critical, which is the number that matters. */
  critical: number;
  high: number;
  /** Where it reaches, largest first. */
  areas: readonly RoleArea[];
};

const categoryOf = (settingId: string): SettingCategory => CATEGORY_OF.get(settingId) ?? ("staff" as SettingCategory);

export function summarise(role: StaffRoleListItem): RoleSummary {
  const counts = new Map<SettingCategory, number>();
  let critical = 0;
  let high = 0;

  for (const grant of role.features) {
    if (grant.sensitivity === "critical") critical += 1;
    if (grant.sensitivity === "high") high += 1;
    const category = categoryOf(grant.settingId);
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }

  const areas = [...counts.entries()]
    .map(([category, count]) => ({ category, count }))
    // Largest first, then by name: the order is stable between renders, and
    // the biggest thing a role does is the first thing read.
    .sort((left, right) => right.count - left.count || left.category.localeCompare(right.category));

  return {
    features: role.features.length,
    permissions: role.permissions.length,
    everything: role.features.length >= TOTAL_FEATURES && TOTAL_FEATURES > 0,
    critical,
    high,
    areas,
  };
}

/** The grants, grouped as the catalogue groups them, for the full list. */
export function byCategory(
  role: StaffRoleListItem,
): readonly { category: SettingCategory; settingIds: readonly string[] }[] {
  const grouped = new Map<SettingCategory, string[]>();
  for (const grant of role.features) {
    const category = categoryOf(grant.settingId);
    const list = grouped.get(category);
    if (list) list.push(grant.settingId);
    else grouped.set(category, [grant.settingId]);
  }
  return [...grouped.entries()]
    .map(([category, settingIds]) => ({ category, settingIds }))
    .sort((left, right) => left.category.localeCompare(right.category));
}

/** How far a role reaches, as one word. */
export type Reach = "critical" | "high" | "low";

/**
 * A role with a critical feature can decide what anybody else may do. That is
 * a different kind of thing from one that reads the dashboard, and the list
 * should say so without being read twice.
 */
export function reachOf(summary: RoleSummary): Reach {
  if (summary.everything || summary.critical > 0) return "critical";
  if (summary.high > 0) return "high";
  return "low";
}

const RANK: Record<Reach, number> = { critical: 0, high: 1, low: 2 };

/**
 * The roles in the order the screen is read: furthest-reaching first.
 *
 * Not alphabetical. "Who can decide what anybody may do" is the question this
 * screen is opened with, and alphabetical answers it last. Within a reach, the
 * one held by most people comes first, then by name so the order is stable.
 */
export function inReachOrder(roles: readonly StaffRoleListItem[]): StaffRoleListItem[] {
  return [...roles].sort((left, right) => {
    const byReach = RANK[reachOf(summarise(left))] - RANK[reachOf(summarise(right))];
    if (byReach !== 0) return byReach;
    return right.memberCount - left.memberCount || left.name.localeCompare(right.name);
  });
}

export type RolesOverview = {
  roles: number;
  /** Roles that can decide what anybody else may do. */
  critical: number;
  /** How many people hold at least one of those, counted once each. */
  peopleWithCritical: number;
  /** Roles nobody holds: either new, or dead. Both are worth knowing. */
  unheld: number;
};

/**
 * The band across the top: the question the screen is opened with, answered
 * before anything else is read.
 *
 * `peopleWithCritical` needs the staff list, because somebody holding two
 * critical roles is one person and adding the member counts would say two.
 */
export function overview(
  roles: readonly StaffRoleListItem[],
  staff: readonly { roles: readonly { staffRoleId: string }[] }[],
): RolesOverview {
  const criticalIds = new Set(
    roles.filter((role) => reachOf(summarise(role)) === "critical").map((role) => role.staffRoleId),
  );
  return {
    roles: roles.length,
    critical: criticalIds.size,
    peopleWithCritical: staff.filter((person) => person.roles.some((each) => criticalIds.has(each.staffRoleId))).length,
    unheld: roles.filter((role) => role.memberCount === 0).length,
  };
}

export type Comparison = {
  /** What the first has and the second does not. */
  onlyLeft: readonly string[];
  onlyRight: readonly string[];
  shared: readonly string[];
};

/** What one role has that another does not, by feature. */
export function compare(left: StaffRoleListItem, right: StaffRoleListItem): Comparison {
  const mine = new Set(left.features.map((grant) => grant.settingId));
  const theirs = new Set(right.features.map((grant) => grant.settingId));
  return {
    onlyLeft: [...mine].filter((id) => !theirs.has(id)).sort(),
    onlyRight: [...theirs].filter((id) => !mine.has(id)).sort(),
    shared: [...mine].filter((id) => theirs.has(id)).sort(),
  };
}
